import { z } from 'zod';

import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';

import {
  type KbbFullVehicle,
  KbbFullVehicleSchema,
  type KbbValuation,
  KbbValuationSchema,
  type KbbVehicleMake,
  KbbVehicleMakeSchema,
  type KbbVehicleModel,
  KbbVehicleModelSchema,
  type KbbVehicleTrimResult,
  KbbVehicleTrimSchema,
  type KbbVehicleYearModel,
  KbbVehicleYearModelIdSchema,
} from './vehicleTypes';

const GetKbbVehicleMakeResponseSchema = z.object({
  resources: z.array(KbbVehicleMakeSchema),
});

const GetKbbVehicleModelResponseSchema = z.object({
  resources: z.array(KbbVehicleModelSchema),
});

const GetKbbVehicleModelYearResponseSchema = z.object({
  resources: z.array(KbbVehicleYearModelIdSchema),
});

const KbbVehicleTrimResponseRawSchema = KbbVehicleTrimSchema.extend({
  trimName: z.string().optional(),
});

const GetKbbVehicleTrimResponseSchema = z.object({
  resources: z
    .array(KbbVehicleTrimResponseRawSchema)
    .transform((array) =>
      array.map((trim) => ({
        ...trim,
        trimName: trim.trimName || 'Base Trim',
      })),
    )
    .pipe(z.array(KbbVehicleTrimSchema)),
});

const KbbFullVehicleResponseRawSchema = KbbFullVehicleSchema.extend({
  trimName: z.string().optional(),
  oemBodyStyle: z.string().optional(),
});

export const GetKbbFullVehicleResponseSchema = z.object({
  resources: z
    .array(KbbFullVehicleResponseRawSchema)
    .transform((array) =>
      array.map((vehicle) => ({
        ...vehicle,
        trimName: vehicle.trimName || 'Base Trim',
        oemBodyStyle: vehicle.oemBodyStyle || 'Unknown',
      })),
    )
    .pipe(z.array(KbbFullVehicleSchema)),
});

const PostKbbValuationResponseSchema = z.object({
  resources: z.array(KbbValuationSchema),
});

export const vehicleApi = {
  async getKbbMakes({ year }: { year: string }): Promise<KbbVehicleMake[]> {
    const requestUrl = `${getServiceUrl()}/vehicles/makes?year=${year}`;
    const response = await httpClientInterface.send('GET', requestUrl);
    return GetKbbVehicleMakeResponseSchema.parse(response.data).resources;
  },

  async getKbbModels({ year, makeId }: { year: string; makeId: number }): Promise<KbbVehicleModel[]> {
    const requestUrl = `${getServiceUrl()}/vehicles/models?year=${year}&makeId=${makeId}`;
    const response = await httpClientInterface.send('GET', requestUrl);
    return GetKbbVehicleModelResponseSchema.parse(response.data).resources;
  },
  async getKbbTrims({
    year,
    makeId,
    modelId,
  }: {
    year: string;
    makeId: number;
    modelId: number;
  }): Promise<KbbVehicleTrimResult> {
    const modelYearUrl = `${getServiceUrl()}/vehicles/modelyears?year=${year}&makeId=${makeId}&modelId=${modelId}`;
    const modelYearResponse = await httpClientInterface.send('GET', modelYearUrl);

    const parsedResponse = GetKbbVehicleModelYearResponseSchema.parse(modelYearResponse.data);
    const modelYearResources: KbbVehicleYearModel[] = parsedResponse.resources;

    const modelYearIdVehicle = modelYearResources[0];
    if (!modelYearIdVehicle) {
      throw new Error('No modelYearId returned.');
    }
    const modelYearId = modelYearIdVehicle.modelYearId;

    const trimUrl = `${getServiceUrl()}/vehicles/trims?modelYearId=${modelYearId}`;
    const trimResponse = await httpClientInterface.send('GET', trimUrl);
    const trims = GetKbbVehicleTrimResponseSchema.parse(trimResponse.data).resources;

    return { modelYearId, trims };
  },

  async getKbbFullVehicle({
    year,
    makeId,
    modelId,
    modelYearId,
    trimId,
  }: {
    year: string;
    makeId: number;
    modelId: number;
    modelYearId?: number; // not required to get vehicle info
    trimId: number;
  }): Promise<KbbFullVehicle[]> {
    const modelYearIdParam = typeof modelYearId === 'number' && modelYearId !== 0 ? `&modelYearId=${modelYearId}` : '';
    const requestUrl = `${getServiceUrl()}/vehicles/vehicles?year=${year}&makeId=${makeId}&modelId=${modelId}${modelYearIdParam}&trimId=${trimId}`;
    const response = await httpClientInterface.send('GET', requestUrl);
    return GetKbbFullVehicleResponseSchema.parse(response.data).resources;
  },

  async getKbbFullVehicleByVin({ vin }: { vin: string }): Promise<KbbFullVehicle[]> {
    const requestUrl = `${getServiceUrl()}/vehicles/search/vin?vin=${vin}`;
    const response = await httpClientInterface.send('GET', requestUrl);
    return GetKbbFullVehicleResponseSchema.parse(response.data).resources;
  },

  async getKbbFullVehicleByVehicleId({ vehicleId }: { vehicleId: number }): Promise<KbbFullVehicle> {
    const requestUrl = `${getServiceUrl()}/vehicles/vehicles/id?vehicleId=${vehicleId}`;
    const response = await httpClientInterface.send('GET', requestUrl);
    return GetKbbFullVehicleResponseSchema.parse({ resources: [response.data] }).resources[0];
  },

  async getKbbValuation({
    vehicleId,
    zipCode,
    miles,
  }: {
    vehicleId: number;
    zipCode: string;
    miles: string;
  }): Promise<KbbValuation[]> {
    const requestUrl = `${getServiceUrl()}/vehicles/valuation`;

    const payload = {
      zipcode: zipCode,
      configuration: {
        vehicleId: vehicleId,
      },
      mileage: +miles,
    };

    const response = await httpClientInterface.send('POST', requestUrl, payload);
    return PostKbbValuationResponseSchema.parse(response.data).resources;
  },
};
